import { useTranslation } from 'react-i18next';
import { useCallback, useEffect, useRef, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { MultiSelectTags } from '@/components/ui/multi-select-tags';
import { Button } from '@/components/ui/button';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { profileApi } from '@/lib/api/profile';
import { codebookApi } from '@/lib/api/codebook';
import { UpdateIndustriesDto } from '@paladin-monorepo/common';
import { SuccessIndicator } from '@/components/ui/success-indicator';

export function SupplierIndustriesCard() {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const [showSuccess, setShowSuccess] = useState(false);
  const hideTimeoutRef = useRef<number | null>(null);

  const [industryIds, setIndustryIds] = useState<string[]>([]);

  const { data: selected = [], isLoading: loadingSelected } = useQuery({
    queryKey: ['me', 'industries'],
    queryFn: () => profileApi.getIndustries(),
  });

  const { data: options = [], isLoading: loadingOptions } = useQuery({
    queryKey: ['codebook', 'industries'],
    queryFn: () => codebookApi.listIndustries(),
  });

  useEffect(() => {
    return () => {
      if (hideTimeoutRef.current) {
        window.clearTimeout(hideTimeoutRef.current);
      }
    };
  }, []);

  useEffect(() => {
    if (selected) {
      setIndustryIds(selected.map((i) => i.id));
    }
  }, [selected]);

  const mutation = useMutation({
    mutationFn: (payload: UpdateIndustriesDto) => profileApi.updateIndustries(payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['me', 'industries'] });
      queryClient.invalidateQueries({ queryKey: ['profile'] });
      setShowSuccess(true);
      if (hideTimeoutRef.current) {
        window.clearTimeout(hideTimeoutRef.current);
      }
      hideTimeoutRef.current = window.setTimeout(() => {
        setShowSuccess(false);
      }, 3000);
    },
  });

  const onSave = useCallback(() => {
    mutation.mutate({ industryIds });
  }, [industryIds, mutation]);

  const optionLabels = options.map((o) => ({
    id: o.id,
    name: t(`projects.industry.options.${o.id}`),
  }));

  return (
    <Card>
      <CardHeader>
        <CardTitle>
          {t('profile.industries.title')}
          <SuccessIndicator visible={showSuccess} />
        </CardTitle>
        <CardDescription>{t('profile.industries.description')}</CardDescription>
      </CardHeader>
      <CardContent>
        {loadingSelected || loadingOptions ? (
          <div>{t('generic.loading')}</div>
        ) : (
          <div className="space-y-6">
            <MultiSelectTags
              label={t('profile.industries.label')}
              value={industryIds}
              onChange={setIndustryIds}
              options={optionLabels}
              placeholder={t('profile.industries.placeholder')}
              filterable
              searchPlaceholder={t('profile.multiSelect.searchPlaceholder')}
              noResultsText={t('profile.multiSelect.noResults')}
            />
            <Button type="button" onClick={onSave} disabled={mutation.isPending}>
              {mutation.isPending ? t('generic.saving') : t('generic.save')}
            </Button>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
